import { recipe } from "@vanilla-extract/recipes";
import { style, keyframes, globalStyle } from "@vanilla-extract/css";
import { rem } from "@huub-web/utils/css/rem";

import { transition } from "../../utils/transition";
import { disabledState } from "../../theme/disabledState";
import { theme } from "../../theme/css/theme.css";
import { groupStyle } from "../Group/Group.css";
import { edgeLeadingStyle } from "../Edges/Edges.css";

export const containerStyle = recipe({
  base: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    textAlign: "left",
    transition: transition(
      "background",
      "border-color",
      "opacity",
      "outline-color",
      "box-shadow",
    ),
    maxWidth: "100%",
    color: theme.colors.onBackground,
    selectors: {
      [`${groupStyle}[data-direction="vertical"] &:first-child:not(:last-child)`]:
        {
          borderBottomRightRadius: "0",
          borderBottomLeftRadius: "0",
        },
      [`${groupStyle}[data-direction="vertical"] &:last-child:not(:first-child)`]:
        {
          borderTopLeftRadius: "0",
          borderTopRightRadius: "0",
        },
      [`${groupStyle}[data-direction="vertical"] &:not(:first-child)`]: {
        marginTop: "-1px",
      },

      [`${groupStyle}[data-direction="horizontal"] &:first-child:not(:last-child)`]:
        {
          borderTopRightRadius: "0",
          borderBottomRightRadius: "0",
        },
      [`${groupStyle}[data-direction="horizontal"] &:last-child:not(:first-child)`]:
        {
          borderTopLeftRadius: "0",
          borderBottomLeftRadius: "0",
        },
      [`${groupStyle}[data-direction="horizontal"] &:not(:first-child)`]: {
        marginLeft: "-1px",
      },

      [`${groupStyle} &:hover`]: {
        zIndex: 2,
      },
    },
  },
  variants: {
    align: {
      center: {
        textAlign: "center",
      },
      right: {
        textAlign: "right",
      },
      left: {
        textAlign: "left",
      },
      justify: {
        textAlign: "justify",
      },
    },
    variant: {
      default: {
        backgroundColor: theme.colors.background,
        "--border-color": theme.colors.inputBorder,
        border: `1px solid ${theme.colors.inputBorder}`,
        selectors: {
          "&[data-error]": {
            "--border-color": theme.colors.error,
            borderColor: theme.colors.error,
          },
          "&[data-success]": {
            "--border-color": theme.colors.success,
            borderColor: theme.colors.success,
          },
          "&[data-highlight]": {
            "--border-color": theme.colors.warning,
            borderColor: theme.colors.warning,
          },
        },
      },
      toggle: {
        border: "1px solid transparent",
        backgroundColor: theme.colors.panel,
        color: theme.colors.onBackground,
        selectors: {
          "&:hover:not([data-active]):not([aria-disabled])": {
            cursor: "pointer",
          },
          "&:hover:not([aria-disabled]), &[data-active]:not([aria-disabled])": {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.primary,
            boxShadow: "0 10px 30px 0 #0000001A",
          },
          "&[data-error]": {
            "--border-color": theme.colors.error,
            borderColor: theme.colors.error,
          },
          "&[data-success]": {
            "--border-color": theme.colors.success,
            borderColor: theme.colors.success,
          },
          "&[data-highlight]": {
            "--border-color": theme.colors.warning,
            borderColor: theme.colors.warning,
          },
        },
      },
      opaque: {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        textUnderlineOffset: rem(2),
        selectors: {
          "&:hover:not([data-active])": {
            textDecoration: "underline",
          },
          "&:hover, &[data-active]": {
            textDecoration: "underline",
          },
        },
      },
    },
    width: {
      stretch: {
        width: "100%",
      },
      auto: {
        width: "auto",
      },
      default: {},
    },
    size: {
      s: {
        borderRadius: theme.borderRadiuses.xxs,
        paddingLeft: rem(12),
        paddingRight: rem(12),
        height: theme.spacings.l,
        selectors: {
          "&[data-label]": {
            padding: `${theme.spacings.xs} ${theme.spacings.s}`,
            height: `calc(${theme.spacings.l} + ${theme.spacings.xs})`,
          },
          "&:has(.--trailing-last)": {
            paddingRight: theme.spacings.xs,
          },
          "&[data-multiline]": {
            paddingTop: theme.spacings.s,
            paddingBottom: theme.spacings.s,
          },
        },
      },
      default: {
        borderRadius: theme.borderRadiuses.xs,
        paddingLeft: theme.spacings.s,
        paddingRight: theme.spacings.s,
        height: theme.spacings.xl,
        selectors: {
          "&:has(.--trailing-last)": {
            paddingRight: theme.spacings.xs,
          },
          // "&:has(.add-on)": {
          //   // padding: `0 ${rem(12)}`,
          // },
          "&[data-multiline]": {
            paddingTop: theme.spacings.s,
            paddingBottom: theme.spacings.s,
          },
          "&[data-label]": {
            gap: theme.spacings.xxs,
            padding: `${rem(11)} ${theme.spacings.s}`,
            height: "auto",
          },
        },
      },
      minimal: {
        gap: theme.spacings.xxs,
      },
    },
    hasHover: {
      true: {
        selectors: {
          "&:hover, &[data-active]": {
            "--border-color": theme.colors.primary,
            borderColor: theme.colors.primary,
          },
          "&:focus-within": {
            "--border-color": theme.colors.primary,
            borderColor: theme.colors.primary,
          },
          "&[data-error]:hover, &[data-active][data-error]": {
            "--border-color": theme.colors.error,
            borderColor: theme.colors.error,
          },
          "&[data-error]:focus-within": {
            "--border-color": theme.colors.error,
            borderColor: theme.colors.error,
          },
          "&[data-success]:hover, &[data-active][data-success]": {
            "--border-color": theme.colors.success,
            borderColor: theme.colors.success,
          },
          "&[data-success]:focus-within": {
            "--border-color": theme.colors.success,
            borderColor: theme.colors.success,
          },
          "&[data-highlight]:hover, &[data-active][data-highlight]": {
            "--border-color": theme.colors.warning,
            borderColor: theme.colors.warning,
          },
          "&[data-highlight]:focus-within": {
            "--border-color": theme.colors.warning,
            borderColor: theme.colors.warning,
          },
        },
      },
    },
    multiLine: {
      true: {
        height: "auto",
      },
    },
    cursor: {
      text: {
        cursor: "text",
      },
      default: {
        cursor: "default",
      },
    },
    disabled: {
      true: {
        ...disabledState,
      },
    },
    hasTrailingLast: {
      true: {
        selectors: {},
      },
    },
    overflowHidden: {
      true: {
        overflow: "hidden",
      },
    },
  },
  defaultVariants: {
    size: "default",
    disabled: false,
    hasHover: true,
    variant: "default",
    width: "default",
  },
});

export const flexStyle = recipe({
  base: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    width: "100%",
  },
  variants: {
    height: {
      true: {
        height: "100%",
      },
    },
    size: {
      s: {
        gap: theme.spacings.xxs,
      },
      default: {
        gap: rem(8),
      },
      minimal: {
        gap: theme.spacings.xxs,
      },
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export const inputStyle = recipe({
  base: {
    border: 0,
    lineHeight: 1,
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    background: "transparent",

    ":focus": {
      outline: "none",
    },
    ":placeholder-shown": {
      textOverflow: "ellipsis",
    },
    selectors: {
      "&[type=number]": {
        MozAppearance: "textfield",
      },
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
      },
    },
  },
  variants: {
    align: {
      center: {
        textAlign: "center",
      },
      right: {
        textAlign: "right",
      },
      left: {
        textAlign: "left",
      },
      justify: {
        textAlign: "justify",
      },
    },
    disabled: {
      true: {
        cursor: "not-allowed",
      },
    },
    proxy: {
      true: {
        width: rem(4),
        height: rem(4),
        opacity: 0,
        position: "absolute",
        top: 0,
        left: 0,
      },
      false: {
        width: "100%",
      },
    },
    fullProxy: {
      true: {
        width: "100%",
        height: "100%",
        position: "absolute",
        opacity: 0,
        top: 0,
        left: 0,
        zIndex: 3,
      },
    },
  },
  defaultVariants: {
    proxy: false,
  },
});

export const placeholderStyle = style({
  "::placeholder": {
    color: theme.colors.placeholder,
    fontStyle: "italic",
  },
});

export const elementPlaceholderStyle = style({
  color: theme.colors.placeholder,
  fontStyle: "italic",
  pointerEvents: "none",
  userSelect: "none",
});

export const edgeContentStyle = style({
  lineHeight: 1,
  overflow: "hidden",
});

export const labelStyle = recipe({
  base: {
    color: theme.colors.placeholder,
    userSelect: "none",
    paddingBottom: theme.spacings.xxs2,
    transition: transition("transform", "opacity"),
    transformOrigin: "top left",
  },
  variants: {
    float: {
      true: {
        transform: "translateY(50%) scale(1.1429)",
      },
    },
  },
});

const pulseKeyframes = keyframes({
  "0%": {
    opacity: 0.6,
    width: "100%",
    height: "100%",
    margin: 0,
  },
  "50%": {
    opacity: 0,
    width: "calc(100% + 20px)",
    height: "calc(100% + 20px)",
    margin: "-10px",
  },
  "100%": {
    opacity: 0,
    width: "100%",
    height: "100%",
    margin: 0,
  },
});

export const highlightStyle = recipe({
  base: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: `1px solid ${theme.colors.warning}`,
    pointerEvents: "none",
    animation: `${pulseKeyframes} 1.5s ease-in-out infinite`,
  },
  variants: {
    size: {
      s: {
        borderRadius: theme.borderRadiuses.xs,
      },
      default: {
        borderRadius: theme.borderRadiuses.s,
      },
      minimal: {},
    },
  },
});

export const contentStyle = recipe({
  base: {
    overflow: "hidden",
    height: "100%",
    width: "100%",
  },
});

export const badgeContainerStyle = style({
  position: "absolute",
  top: 0,
  right: 0,
});

export const badgeStyle = style({
  // transform: "translate(-70%, -30%)",
  transform: "translate(30%, -30%)",
});

export const infoIconStyle = style({
  cursor: "default",
});

globalStyle(`${edgeLeadingStyle}.with-float *`, {
  opacity: 0,
});
